import { Observable } from "@microsoft/fast-element";
import type { Constructable, FASTElement } from "@microsoft/fast-element";

type HostAttributeAware = FASTElement & HTMLElement;

export type ConstructableHostAttributeAware = Constructable<HostAttributeAware>;

export interface HostAttributeStripper {
    getStrippedHostAttribute(attributeName: string): string | null;
}

export type HostAttributeStripperType<TBase extends ConstructableHostAttributeAware> =
    Constructable<InstanceType<TBase> & HostAttributeStripper> & TBase;

export interface HostAttributeStripperConstructor {
    hostAttributesToStrip?: readonly string[];
}

export function HostAttributeStripper<TBase extends ConstructableHostAttributeAware>(Base: TBase): HostAttributeStripperType<TBase> {
    class HostAttributeStripperBase extends Base {
        private suppressHostAttributeStrip = false;
        private readonly strippedHostAttributeValues = new Map<string, string | null>();

        public attributeChangedCallback(attributeName: string, oldValue: string | null, newValue: string | null): void {
            super.attributeChangedCallback(attributeName, oldValue, newValue);

            const constructorWithConfig = this.constructor as HostAttributeStripperConstructor;
            const hostAttributesToStrip = constructorWithConfig.hostAttributesToStrip || [];

            if (!hostAttributesToStrip.includes(attributeName) || this.suppressHostAttributeStrip)
                return;

            const normalizedValue = newValue?.trim() || null;

            if (normalizedValue !== null)
                this.strippedHostAttributeValues.set(attributeName, normalizedValue);
            else
                this.strippedHostAttributeValues.delete(attributeName);

            const propertyName = this.attributeNameToPropertyName(attributeName);
            Observable.notify(this, propertyName);

            if (newValue === null)
                return;

            this.suppressHostAttributeStrip = true;

            try {
                this.removeAttribute(attributeName);
            } finally {
                this.suppressHostAttributeStrip = false;
            }
        }

        public getStrippedHostAttribute(attributeName: string): string | null {
            return this.strippedHostAttributeValues.get(attributeName) ?? null;
        }

        private attributeNameToPropertyName(attributeName: string): string {
            return attributeName
                .split("-")
                .map((part, index) => index === 0
                    ? part
                    : `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
                .join("");
        }
    }

    return HostAttributeStripperBase as unknown as HostAttributeStripperType<TBase>;
}
